/* storybook-check-ignore */
import {
  Property,
  SellRequestState,
  PropertyPoolType,
  PropertyCard,
} from '@opendoor/cinderblocks/buy/PropertyCards/PropertyCard';
import { HOMEPAGE_URL } from '../../../helpers/globals';
import {
  GetBwodListingPropertyInfoQuery,
  OdProtosRealtyOdlsData_SellRequest_State,
} from '__generated__/athena';
import { Box, Flex, Text } from '@opendoor/bricks/core';
import { COSMOS_URL } from 'components/globals';

interface SimilarListingsProps {
  properties: Array<Property>;
  market?: string;
}

export const formatSimilarListingsForPropertyCards = (
  property: GetBwodListingPropertyInfoQuery['property']['property'],
): Array<Property> => {
  if (!property?.similarListings?.results?.nodes) {
    return [];
  }
  return property?.similarListings?.results?.nodes?.map((item) => {
    return {
      id: item.addressId,
      addressId: item.addressId,
      paramsId: item.displaySlug,
      bedrooms: item.bedrooms || 0,
      bathroomsTotal: item.bathroomsTotal || 0,
      currentListPrice: item.listPrice ? item.listPrice / 100 : 0, // TODO: is this diff from listPrice?
      publishedAt: item.listTimestamp,
      street: item.street,
      state: item.state,
      city: item.city,
      badges: [],
      photos:
        item.photos?.nodes?.map((item) => {
          if (item?.urls?.resizable) {
            return {
              urls: {
                size768x512: `${item?.urls?.resizable}?${new URLSearchParams({
                  preset: '3:2-sm',
                  service: 'cosmos',
                }).toString()}`,
                lazy: true,
              },
            };
          }
          return { urls: null };
        }) || null,
      thumbnailLegalLogo: '', // TODO
      thumbnailLegalText: '', // TODO
      listPrice: item.listPrice ? item.listPrice / 100 : 0,
      latitude: item.latitude || 0,
      longitude: item.longitude || 0,
      currentListingState: getSellRequestState(item.status),
      yearBuilt: 0, // TODO
      isFavorite: null,
      postalCode: item.postalCode,
      lotSqft: item.sqFtLot || 0,
      poolType: PropertyPoolType.NONE, // TODO
      totalLivingSqFt: item.sqFtTotalLiving || 0,
      soldPrice: null,
      openHouses: [{ startsAt: item.nextOpenHouse?.startsAt }],
    };
  });
};

export const getSellRequestState = (
  status?: OdProtosRealtyOdlsData_SellRequest_State | null,
): SellRequestState => {
  switch (status) {
    case 'SELL_REQUEST_ACTIVE':
      return SellRequestState.SELL_REQUEST_ACTIVE;
    case 'SELL_REQUEST_CLOSED':
      return SellRequestState.SELL_REQUEST_CLOSED;
    case 'SELL_REQUEST_COMING_SOON':
      return SellRequestState.SELL_REQUEST_COMING_SOON;
    case 'SELL_REQUEST_EXPIRED':
      return SellRequestState.SELL_REQUEST_EXPIRED;
    case 'SELL_REQUEST_SALE_PENDING':
      return SellRequestState.SELL_REQUEST_SALE_PENDING;
    case 'SELL_REQUEST_TEMP_OFF_MARKET':
      return SellRequestState.SELL_REQUEST_TEMP_OFF_MARKET;
    case 'SELL_REQUEST_WITHDRAWN':
      return SellRequestState.SELL_REQUEST_WITHDRAWN;
    default:
      return SellRequestState.SELL_REQUEST_UNKNOWN;
  }
};

const isPublishedListing = (publishedAt: string | null): boolean => {
  try {
    return !!(publishedAt && new Date(publishedAt));
  } catch {
    return false;
  }
};

const SimilarListings = ({ properties }: SimilarListingsProps) => {
  if (!properties) {
    return null;
  }
  return (
    <>
      <Text as="h2" fontSize="s3" lineHeight="s3" fontWeight="bold">
        Similar Listings
      </Text>
      <ul
        style={{
          listStyle: 'none',
          marginLeft: '0',
          paddingLeft: '0',
          paddingBottom: '25px',
          marginBottom: '10px',
        }}
      >
        <Flex
          position="relative"
          flexWrap={['nowrap', null, null, 'wrap']}
          overflowX="auto"
          gridGap={['4', null, '9']}
          paddingTop={'4'}
          px="3"
          paddingBottom={'4'}
          width="100%"
        >
          {properties.map((p) => {
            return (
              isPublishedListing(p.publishedAt) && (
                <Box
                  as="li"
                  key={p.addressId}
                  minWidth={[null, null, '280px']}
                  minHeight={[null, null, '245px']}
                  px="2"
                  py="3"
                >
                  <PropertyCard
                    key={p.addressId}
                    property={p}
                    homepageUrl={HOMEPAGE_URL}
                    useMlsPropertyCardEmitter={() => {}}
                    hrefOverride={`${COSMOS_URL}/properties/${p.paramsId}/aid_${p.addressId}`}
                  />
                </Box>
              )
            );
          })}
        </Flex>
      </ul>
    </>
  );
};

export default SimilarListings;
